import React, { useMemo } from "react";

import type { ICohortQueryModel } from "@performyard/models";
import { Text } from "@performyard/py-components";
import { DEFAULT_TIMEZONE } from "@performyard/utils-isomorphic";

import { EmployeeName } from "../../../../../../components/EmployeeQueryBuilder/EmployeeName";
import {
	getComparatorLabel,
	getDisplayValue,
	getFieldName,
	getPersonOid,
} from "../../../../../../components/EmployeeQueryBuilder/utils";

import { useGroupsInternalApi } from "../../../../../../api/hooks";
import { useCompanySettings } from "../../../../../../api/hooks/defs/company";
import { useEmployeeFields } from "../../../../../../api/hooks/defs/employee-fields";

type AutomationRulesDisplayProps = {
	userQuery: ICohortQueryModel | null;
};

export const AutomationRulesDisplay = ({ userQuery }: AutomationRulesDisplayProps) => {
	const { employeeFields } = useEmployeeFields();
	const { data: groups } = useGroupsInternalApi();
	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;

	const displayNames = useMemo(() => {
		const names: Record<string, string> = {};
		for (const group of groups ?? []) {
			names[group.id] = group.name;
		}
		return names;
	}, [groups]);

	if (!userQuery || userQuery.and.length === 0) {
		return (
			<Text size="paragraph-3" color="neutral-7">
				No rules configured
			</Text>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			{userQuery.and.map((group, groupIndex) => (
				<React.Fragment key={groupIndex}>
					{group.or.map((clause, clauseIndex) => {
						const fieldName = getFieldName(clause.field, employeeFields);
						const comparatorLabel = getComparatorLabel(clause.comparator, clause.value);
						const personOid = getPersonOid(clause, employeeFields);
						const displayValue = getDisplayValue(clause, employeeFields, displayNames, companyTimezone);
						return (
							<Text key={clauseIndex} size="paragraph-3" color="neutral-9">
								{fieldName} - {comparatorLabel}
								{personOid ? (
									<>
										{" - "}
										<EmployeeName employeeId={personOid} />
									</>
								) : displayValue ? (
									` - ${displayValue}`
								) : (
									""
								)}
							</Text>
						);
					})}
					{groupIndex < userQuery.and.length - 1 && (
						<Text size="paragraph-3" color="neutral-7">
							and
						</Text>
					)}
				</React.Fragment>
			))}
		</div>
	);
};
